import {Button, Divider, Tab, TabId, Tabs} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {duotone} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useCallback, useContext, useState} from "react";
import {Prompt} from "react-router-dom";
import {AuthContext} from "../providers/AuthProvider";
import ColorizedIndicators from "../components/settings/ColorSettings/ColorizedIndicators/ColorizedIndicators";
import ServiceTypeColor from "../components/settings/ColorSettings/ServiceTypeColor/ServiceTypeColor";
import {
  ServiceTypeColorContext,
  ServiceTypeColorDispatchContext
} from "../components/settings/ColorSettings/ServiceTypeColor/ServiceTypeColorStateProvider";
import ETA from "../components/settings/ETA/eta-general/ETA";
import General from "../components/settings/CompanySettings/general/General";
import Optimization from "../components/settings/CompanySettings/optimization/Optimization";
import UserManagement from "../components/settings/UserManagement/UserManagement";
import UserSettings from "../components/settings/UserSettings";
import VehicleConfigurationView from "../components/settings/VehicleConfiguration/VehicleConfigurationView";
import {FeatureFlagContext} from "../providers/FeatureFlagProvider";
import {ETANotificationConfigView} from "../components/settings/ETA/eta-notification-configs/ETANotificationConfig";

const Container = styled.div`
  width: 100%;
  height: 100%;
  font-family: "Roboto", serif;
`;

const MenuPanelTitle = styled.h2`
  color: #8da4be;
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  margin: 5px 20px 15px;
`;

const SettingsTabs = styled(Tabs)`
  height: 100%;
  width: 100%;

  & .bp5-tab-panel {
    max-width: 100% !important;
    overflow: hidden;
    padding: 0 !important;
  }

  & .bp5-tab-list {
    padding: 20px;
    width: 220px;
    height: 100%;
    box-shadow: 0px 4px 4px 0px #00000040;
    gap: 10px;

    & .bp5-tab {
      font-size: 16px;
      font-weight: 400;
    }
  }
`;

const StyledDivider = styled(Divider)`
  width: 196px;
  margin: 0 0 0 -18px;
`;

const StyledSettingsText = styled.span`
  text-transform: uppercase;

  &::before {
    content: &nbsp;
  }
`;

const Menu = styled(Button)`
  width: 100%;
  justify-content: space-between;
  font-size: 16px;
  :hover {
    color: #215db0 !important;
    background: none !important;

    .bp5-icon {
      color: #215db0 !important;
    }
  }
`;

const Collapser = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const SubTabsItem = styled(Button)<{isSelected: boolean}>`
  width: 100%;
  padding: 0 0 0 15px;
  justify-content: left;
  font-size: 16px;
  color: ${(props) => (props.isSelected ? "#161616 !important" : "#797979 !important")};
  background: ${(props) => (props.isSelected ? "#0038ff1a !important" : "")};
  :hover {
    color: #215db0 !important;
    background: ${(props) => (props.isSelected ? "#0038ff1a !important" : "none !important")};
  }
`;

const SettingsView = () => {
  const {isAdmin} = useContext(AuthContext);
  const [panelTabId, setPanelTabId] = useState<TabId>("user-settings");
  const [isColorSettingsCollapsed, setIsColorSettingsCollapsed] = useState<boolean>(false);
  const [isCompanySettingsCollapsed, setIsCompanySettingsCollapsed] = useState<boolean>(false);
  const [isETASettingsCollapsed, setIsETASettingsCollapsed] = useState<boolean>(false);
  const {isDataChanged} = useContext(ServiceTypeColorContext);
  const serviceTypeColorDispatch = useContext(ServiceTypeColorDispatchContext);
  const {etas: etaEnable, etaNotifications: etaNotificationsEnabled} = useContext(FeatureFlagContext);

  const handleOnChangeTab = useCallback(
    (navbarTabId: TabId) => {
      if (isDataChanged) {
        serviceTypeColorDispatch({
          type: "SHOW_CONFIRM_WITHOUT_SAVING",
          payload: {value: true, redirectFunc: setPanelTabId, redirectParams: navbarTabId}
        });
        return;
      }
      setPanelTabId((prevState) => {
        return prevState === navbarTabId ? prevState : navbarTabId;
      });
    },
    [isDataChanged, serviceTypeColorDispatch]
  );

  const handleCollapseColorSettingsMenu = useCallback(() => {
    setIsColorSettingsCollapsed(!isColorSettingsCollapsed);
  }, [isColorSettingsCollapsed]);

  const handleCollapseCompanySettingsMenu = useCallback(() => {
    setIsCompanySettingsCollapsed(!isCompanySettingsCollapsed);
  }, [isCompanySettingsCollapsed]);

  const handleCollapseETASettingsMenu = useCallback(() => {
    setIsETASettingsCollapsed(!isETASettingsCollapsed);
  }, [isETASettingsCollapsed]);

  return (
    <Container>
      <Prompt
        when={isDataChanged}
        message={"You have unsaved adjustments that will be lost if you Leave this page without saving."}
      />
      <SettingsTabs
        key={panelTabId}
        animate
        vertical
        selectedTabId={panelTabId}
        onChange={handleOnChangeTab}
        renderActiveTabPanelOnly={true}
        className="setting-tabs"
      >
        <MenuPanelTitle>
          <FontAwesomeIcon icon={duotone("gear")} size={"1x"} color={"#8DA4BE"} />
          <StyledSettingsText>Settings</StyledSettingsText>
        </MenuPanelTitle>
        <StyledDivider />
        <Tab id="user-settings" title="User Settings" panel={<UserSettings />} />
        {isAdmin && <Tab id="user-management" title="User Management" panel={<UserManagement />} />}
        {isAdmin && (
          <Tab id="vehicle-configuration" title="Vehicle Configuration" panel={<VehicleConfigurationView />} />
        )}
        {isAdmin && (
          <>
            <Menu
              variant="minimal"
              onClick={handleCollapseColorSettingsMenu}
              endIcon={isColorSettingsCollapsed ? "chevron-up" : "chevron-down"}
              name="Color Settings"
            >
              Color Settings
            </Menu>
            {isColorSettingsCollapsed && (
              <Collapser>
                <SubTabsItem
                  isSelected={panelTabId === "colorized-indicators"}
                  variant="minimal"
                  name="Colorized Indicators"
                  onClick={() => handleOnChangeTab("colorized-indicators")}
                >
                  Colorized Indicators
                </SubTabsItem>
                <SubTabsItem
                  isSelected={panelTabId === "service-type-color"}
                  variant="minimal"
                  name="Service Type Color"
                  onClick={() => handleOnChangeTab("service-type-color")}
                >
                  Service Type Color
                </SubTabsItem>
              </Collapser>
            )}
          </>
        )}
        {isAdmin && (
          <>
            {etaEnable && (
              <>
                <Menu
                  variant="minimal"
                  onClick={handleCollapseETASettingsMenu}
                  endIcon={isETASettingsCollapsed ? "chevron-up" : "chevron-down"}
                  name="ETA Settings"
                  data-testid="eta-settings-menu"
                >
                  ETA Settings
                </Menu>
                {isETASettingsCollapsed && (
                  <Collapser>
                    <SubTabsItem
                      isSelected={panelTabId === "eta-settings-general"}
                      variant="minimal"
                      name="ETA General"
                      onClick={() => handleOnChangeTab("eta-settings-general")}
                      data-testid="eta-settings-general-subtab"
                    >
                      General
                    </SubTabsItem>
                    {etaNotificationsEnabled && (
                      <SubTabsItem
                        isSelected={panelTabId === "eta-notification-settings"}
                        variant="minimal"
                        name="ETA Notification"
                        onClick={() => handleOnChangeTab("eta-notification-settings")}
                        data-testid="eta-notification-settings-subtab"
                      >
                        Customer Notifications
                      </SubTabsItem>
                    )}
                  </Collapser>
                )}
              </>
            )}
            <Menu
              variant="minimal"
              onClick={handleCollapseCompanySettingsMenu}
              endIcon={isCompanySettingsCollapsed ? "chevron-up" : "chevron-down"}
              name="Company Settings"
              data-testid="company-settings-menu"
            >
              Company Settings
            </Menu>
            {isCompanySettingsCollapsed && (
              <Collapser>
                <SubTabsItem
                  isSelected={panelTabId === "company-settings-general"}
                  variant="minimal"
                  name="General"
                  onClick={() => handleOnChangeTab("company-settings-general")}
                  data-testid="company-settings-general-subtab"
                >
                  General
                </SubTabsItem>
                <SubTabsItem
                  isSelected={panelTabId === "company-settings-optimization"}
                  variant="minimal"
                  name="Optimization"
                  onClick={() => handleOnChangeTab("company-settings-optimization")}
                  data-testid="company-settings-optimization-subtab"
                >
                  Optimization
                </SubTabsItem>
              </Collapser>
            )}
          </>
        )}
        <Tab id="company-settings-general" panel={<General />} />
        <Tab id="company-settings-optimization" panel={<Optimization />} />
        <Tab id="eta-settings-general" panel={<ETA />} />
        <Tab id="eta-notification-settings" panel={<ETANotificationConfigView />} />
        <Tab id="colorized-indicators" panel={<ColorizedIndicators />} />
        <Tab id="service-type-color" panel={<ServiceTypeColor />} />
      </SettingsTabs>
    </Container>
  );
};

export default SettingsView;
